// src/components/League/battleArena/ClimbArena.jsx
//
// League Battle View V2 — THE HERO. The competition as a living climb: four
// players as positional presences ascending toward the summit, the leader
// crowned, the ranked cut line drawn, your teal presence the one to track.
// Altitude = composite (points, never percent — the callouts format via
// ArenaCount/fmtScore). When a star pops in the dock below, the points fly UP
// here and your presence surges.
//
// Translated from the locked Claude Design (battle-climb), re-skinned onto the
// shared League palette. Obsidian; reduced-motion safe (ambient motion is CSS the
// global guard neutralizes; the fly-up + surge are JS-gated and simply omit).

import React from 'react';
import { Mono, LIcon } from '../LeagueParts';
import { LTOKENS, LX, alpha, MONO } from '../leagueTokens';
import { fmtPoints } from '../../../utils/leagueFormat';
import { ArenaCount } from './ArenaPrimitives';
import { ST_GOOD } from './arenaTheme';
import { frameDayIdx, isLevelBoard } from './arenaStateMap';
import { prefersReducedMotion } from './arenaEngineCore';
import AgentPresence, { archetypeToDisposition, standingFromRank } from '../../AgentPresence';
import { isAgentPresenceOn } from '../../../config/featureFlags';
// Head layout (sizes + the face-centre lift + the label gap) lives in a non-component
// module so this view file exports only a component, and the collision test reads the
// SAME sizes the component renders. Flag-off renders the orb byte-identically.
import { HEAD_FACE_LIFT, LABEL_BELOW_GAP, COMPACT_AXIS_W, headSizeFor } from './climbHeadLayout';
// The ONE altitude resolver — buildArenaModel's youRank reads the SAME function,
// so the orb altitude/rank/cut here can never disagree with the model's standing
// (B3 lockstep). `liveComposites` arrives already flag-gated (null off-gate).
import { seatAltitude } from './seatAltitude';
import { ArenaAtmosphere } from './arenaAtmosphere';
import { AGENT_FORM_LEAGUE_ENABLED } from '../../../config/agentFormFlags';
import { LeagueAgentFormSlot } from './leagueAgentFormSlot';

// the living atmosphere — deep sky, drifting aurora, a deterministic star field.
export function ClimbArena({ state, mode, seats, climb, youId, w, h, surge, onPlayer, dayIdx: dayIdxProp = null, compact = false, youLiveScore = null, liveComposites = null, voided = false }) {
  const live = state === 'live'; const calm = state === 'awaiting';
  const ranked = mode === 'ranked';
  // Real data supplies the true last-banked index; the fixture preview falls back
  // to frameDayIdx(state).
  const dayIdx = dayIdxProp != null ? dayIdxProp : frameDayIdx(state);
  const tone = ranked ? LTOKENS.gold : LTOKENS.teal;

  const rows = seats.map((s) => ({ ...s, scores: climb[s.id] || [] }));
  const lastIdx = calm ? 0 : dayIdx;
  // Every seat's CURRENT altitude resolves through the ONE seatAltitude ruler
  // (Option X / B3 lockstep — the SAME function buildArenaModel's youRank uses):
  // YOUR seat reads youLiveScore (the per-tick client path); a RIVAL reads its
  // endpoint live composite from `liveComposites` when the orb is live; otherwise
  // the banked series. `liveComposites` arrives already flag-gated (null off-gate →
  // rivals banked, byte-identical to today). Feeds the orb number, altitude, rank
  // and cut uniformly (all through `at`), so they never disagree.
  const at = (s) => seatAltitude(s.id, { youId, youLiveScore, liveComposites, banked: s.scores[lastIdx] ?? 0 });
  // A seat lifts OFF its banked series this tick when a live source drives it —
  // your youLiveScore, or a rival's live composite. Its banked close at lastIdx
  // would otherwise vanish from the trail, so include it as a dot (below).
  const liftsOff = (s) => (s.id === youId ? youLiveScore != null : Number.isFinite(liveComposites?.[s.id]));
  const ranking = [...rows].sort((a, b) => at(b) - at(a));
  const leaderId = ranking[0]?.id;
  const rankOf = (id) => ranking.findIndex((s) => s.id === id) + 1;
  const youRow = rows.find((s) => s.id === youId);

  // geometry — `compact` tightens the axis + vertical padding for the ~374-wide
  // mobile hero. Default off → the desktop hero geometry is byte-identical.
  const axisW = compact ? COMPACT_AXIS_W : 52; const padT = compact ? 32 : 40; const padB = compact ? 54 : 62;
  const plotT = padT; const plotB = h - padB;
  const laneW = (w - axisW) / rows.length;
  const laneX = (i) => axisW + laneW * (i + 0.5);
  const vals = rows.map(at);
  const dMin = Math.min(...vals); const dMax = Math.max(...vals);
  // "At rest" = the climb is still at the start line: the awaiting state, OR every
  // seat still sitting at zero (Day 0 — a live BATTLE group pre-first-close renders
  // as 'live', not 'awaiting', so !calm alone doesn't catch it). The all-ZERO test
  // is deliberate, NOT a bare dMax===dMin tie: four seats that have climbed to the
  // same non-zero altitude HAVE separated from the start and must plot at altitude
  // (collapsing them to base camp would contradict their non-zero value labels). At
  // rest we seat the orbs in a base-camp row below the zero line and draw NO cut or
  // trails, so the orbs never pile onto the zero/cut altitude and nothing crosses
  // the cut label. Once any score moves, the live layout takes over.
  // L-A follow-up (B): a VOIDED cohort reads AT REST too — its banked scores are
  // the contaminated numbers the void quarantines, so we must NOT plot altitude
  // BY them (that would re-assert the very standing the void denies, "computed
  // from the contaminated Day-8 numbers"). Base-camp seating + no trails/cut, with
  // the signifier suppressions above (crown/rank/score) and the caption below, so
  // the board asserts no result. Gated on `voided` → non-voided is byte-identical.
  const atRest = calm || voided || (dMax === 0 && dMin === 0);
  // The one gate on a leader: the crown, the halo, the enlarged head and the figures'
  // mood all read it (§9). "At rest" above is where the seats SIT, and stays all-zero;
  // this is whether anyone LEADS, and a level board at any altitude has no leader.
  const hasLeader = !calm && !voided && !isLevelBoard(vals);
  const DOM = atRest ? [-3, 11] : [dMin - 1.5, dMax + 0.8];
  const Y = (a) => plotB - ((a - DOM[0]) / (DOM[1] - DOM[0])) * (plotB - plotT);

  // the cut sits midway between 2nd and 3rd; only a pod of ≥3 has a meaningful cut,
  // so a sparse/partial pod (live data) neither computes nor draws one. Suppressed
  // at rest too: with no spread, a cut at everyone's altitude is meaningless and
  // would land in the orb band.
  // L-A follow-up (B): a VOIDED cohort asserts NO standing — drop the ranked cut
  // (the "TOP 2 ADVANCE" line is a placement claim the voided panel denies).
  const hasCut = ranked && !atRest && !voided && ranking.length >= 3;
  const cutAlt = ranking.length >= 3 ? (at(ranking[1]) + at(ranking[2])) / 2 : 0;
  const span = DOM[1] - DOM[0];
  const gstep = span > 9 ? 3 : span > 5 ? 2 : 1;
  const gridVals = [];
  for (let g = Math.ceil(DOM[0] / gstep) * gstep; g <= DOM[1] - 0.4; g += gstep) gridVals.push(g);

  const reduce = prefersReducedMotion();
  // Agent Presence heads replace the orbs when the flag is on. Player seat (s.id===youId)
  // = REACTIVE (mood glides with standingFromRank of the SAME rankOf/altitude the orb
  // shows — §9 by construction). The three rivals = STATIC (paint-once, no rAF/breath;
  // accent from the seat colour). Rival disposition comes from s.arch — omitted in LIVE
  // owner-only data (→ neutral), though the fixtures preview does supply rival archetypes.
  const showHead = isAgentPresenceOn();

  return (
    <div style={{ position: 'relative', width: w, height: h, overflow: 'hidden', borderRadius: 18, background: LTOKENS.bg, border: `1px solid ${LTOKENS.hair}` }}>
      <ArenaAtmosphere tone={tone} />
      <div style={{ position: 'absolute', top: -46, left: '50%', transform: 'translateX(-50%)', width: '64%', height: 170,
        background: `radial-gradient(circle, ${alpha(tone, 0.16)}, transparent 70%)`, pointerEvents: 'none' }} />

      <svg width={w} height={h} style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
        {gridVals.map((g) => (
          <g key={g}>
            <line x1={axisW} y1={Y(g)} x2={w} y2={Y(g)} stroke={g === 0 ? LTOKENS.hair2 : LTOKENS.hair} strokeWidth={1} strokeDasharray={g === 0 ? '0' : '1 6'} />
            <text x={axisW - 7} y={Y(g) + 3.5} textAnchor="end" fontFamily={MONO} fontSize={10} fill={LTOKENS.ink3}>{g > 0 ? '+' : ''}{g}</text>
          </g>
        ))}
        {hasCut && (
          <g>
            <rect x={axisW} y={plotT} width={w - axisW} height={Y(cutAlt) - plotT} fill={alpha(LX.cut, 0.045)} />
            <line x1={axisW} y1={Y(cutAlt)} x2={w} y2={Y(cutAlt)} stroke={alpha(LX.cut, 0.55)} strokeWidth={1.3} strokeDasharray="8 5" />
          </g>
        )}
        {/* climb trails — gated on !atRest (not just !calm): at the all-tied start
            there is no climb yet, and a trail from the zero line would float above
            the base-camp orbs. They appear once scores spread. */}
        {!atRest && rows.map((s, i) => {
          const x = laneX(i); const yTop = Y(at(s));
          return (
            <g key={s.id}>
              <defs>
                <linearGradient id={`bv2cl${s.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={s.id === youId ? 0.7 : 0.5} />
                  <stop offset="100%" stopColor={s.color} stopOpacity="0" />
                </linearGradient>
              </defs>
              <line x1={x} y1={yTop} x2={x} y2={plotB} stroke={`url(#bv2cl${s.id})`} strokeWidth={s.id === youId ? 4 : 2.5} strokeLinecap="round" />
              {/* Trail dots = the seat's prior banked closes. A banked seat's orb
                  sits AT its scores[lastIdx], so its dots stop before lastIdx; but any
                  seat that lifts OFF scores[lastIdx] this tick (your youLiveScore, or a
                  rival's live composite) would drop that banked close from the trail —
                  include it as a dot so the trail stays whole. */}
              {live && s.scores.slice(0, liftsOff(s) ? lastIdx + 1 : lastIdx).map((v, k) => (
                <circle key={k} cx={x} cy={Y(v)} r={2.2} fill={LTOKENS.bg} stroke={s.color} strokeWidth={1.3} strokeOpacity={0.5} />
              ))}
            </g>
          );
        })}
      </svg>

      {/* the presences */}
      {rows.map((s, i) => {
        // L-A follow-up (B): a VOIDED board asserts NO standing, so the LEADER
        // emphasis collapses at its single source — gating `lead` on hasLeader drops
        // the crown, the leader halo ring, and the enlarged leader orb/head all at
        // once (leaderId is sorted by the contaminated banked altitude, so any
        // leader tell would re-assert 'who is winning'). The `you` marker stays: it
        // is identity ("which orb is me"), not a placement. A level board collapses
        // the same way: its leaderId is only the first seat of a tie.
        const you = s.id === youId; const lead = s.id === leaderId && hasLeader;
        const x = laneX(i); const y = atRest ? plotB - 16 : Y(at(s));
        const sz = compact ? (you ? 44 : lead ? 40 : 36) : (you ? 52 : lead ? 46 : 40);
        // Heads render LARGER than the orb for a legible mood read. `sz` stays the orb
        // size (flag-off byte-identical); `headSz` is the head size; `dz` is whichever is
        // actually shown, so the halo / crown / beside-label chrome tracks it.
        const headSz = headSizeFor(you, lead, compact);
        const dz = showHead ? headSz : sz;
        const crownSize = showHead ? Math.round(dz * 0.34) : (compact ? 15 : 17);
        const rk = rankOf(s.id);
        const bob = !calm && !reduce ? (you ? 'bv2-bob-you' : 'bv2-bob') : '';
        return (
          <div key={s.id}>
            {/* Under-orb labels are DESKTOP-only. The mobile (compact) climb drops
                the name row entirely — four names won't fit a ~374px row without
                bunching, and identity is already carried by the distinct orb color +
                rank + the leader's crown + the rival tap (OpponentSnapshot). */}
            {!compact && (
              <div style={{ position: 'absolute', left: x, top: plotB + 14, transform: 'translateX(-50%)', textAlign: 'center', whiteSpace: 'nowrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 3, background: s.color, boxShadow: you ? `0 0 6px ${alpha(s.color, 0.8)}` : 'none' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: you ? s.color : LTOKENS.ink2 }}>{s.name}</span>
                  {you && <Mono style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: s.color }}>YOU</Mono>}
                </span>
                {/* Desktop keeps the your-own archetype line (rivals never carry
                    arch → never fabricated; a CPU's archetype lives in its name). */}
                {s.arch && <Mono style={{ fontSize: 8.5, color: LTOKENS.ink3, marginTop: 1, display: 'block' }}>{s.arch}</Mono>}
              </div>
            )}

            {/* centering lives on the outer node; the bob + surge animate an inner
                wrapper each (they set translateY, which must not clobber the
                -50%,-50% centering — the design prototype's latent shift bug). */}
            <div onClick={onPlayer && !you && !calm ? () => onPlayer(s.id) : undefined}
              style={{ position: 'absolute', left: x, top: y, transform: 'translate(-50%,-50%)', cursor: onPlayer && !you && !calm ? 'pointer' : 'default' }}>
              {/* A static CPU head drops its own bob float — the head itself is motionless
                  (finding 13; the leader halo is arena chrome and is kept). The player head
                  and the flag-off orb keep the float. */}
              <div className={showHead && !you ? '' : bob} style={{ position: 'relative' }}>
                {/* The surge `key` REMOUNTS this subtree to retrigger the CSS scale-pulse —
                    fine for the flag-off orb, but it would tear down + rebuild the player
                    HEAD on every beat (new FaceCtl, glide discarded). So the key + pulse
                    class apply to the ORB path only; the head keeps its identity and its
                    mood glide across surges (the fly-up token + beat caption still fire). */}
                <div className={!showHead && you && surge && surge.key && !reduce ? 'bv2-surge' : ''} key={!showHead && you && surge ? surge.key : undefined} style={{ position: 'relative' }}>
                  {!calm && (you || lead) && !reduce && (
                    <div className="bv2-halo" style={{ position: 'absolute', left: '50%', top: '50%', width: dz + 14, height: dz + 14, borderRadius: '50%', border: `1.5px solid ${s.color}`, pointerEvents: 'none' }} />
                  )}
                  {showHead ? (
                    /* HEAD — replaces the orb+digit, rendered at headSz (larger than the
                       orb for legibility). Footprint is headSz×headSz so the crown, halo
                       and label anchors track the head; the FACE is centred on (x,y) — the
                       P&L anchor — via the HEAD_FACE_LIFT up-shift (the score label below is
                       decoration offset from this, never the anchor). pointer-events:none so
                       the rival tap (onPlayer) and the training-preview <button> still get
                       the click. The rank digit is intentionally dropped — altitude + crown
                       + the score label carry rank; the head carries mood-at-a-glance. */
                    <div style={{ width: headSz, height: headSz, position: 'relative', pointerEvents: 'none' }}>
                      {/* Agent form build S2: the seat's form, in the head's footprint and centred on its anchor (the
                          head's lift is the robot's own). The lanes have a direction, so an opponent looks toward the
                          player (ruling R14). Inside this branch, so the surge key never remounts the view. */}
                      {AGENT_FORM_LEAGUE_ENABLED ? (
                        <LeagueAgentFormSlot
                          seat={s}
                          you={you}
                          size={headSz}
                          reducedMotion={reduce}
                          standing={{ rank: rk, seats: rows.length, cut: hasCut, shown: hasLeader, complete: state === 'complete' }}
                          facing={you || !youRow ? undefined : i < rows.indexOf(youRow) ? 'right' : 'left'}
                        />
                      ) : (
                      <div style={{ position: 'absolute', left: '50%', top: '50%',
                        transform: `translate(-50%, calc(-50% - ${(HEAD_FACE_LIFT * headSz).toFixed(1)}px))`,
                        width: headSz * 140 / 156, height: headSz }}>
                        <AgentPresence
                          disposition={archetypeToDisposition(s.arch)}
                          accent={s.color}
                          standing={you && hasLeader ? standingFromRank(rk, rows.length) : 0}
                          size={headSz}
                          enableEnvironment={false}
                          radial={false}
                          reactivityLevel={you ? 'reactive' : 'static'}
                        />
                      </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ width: sz, height: sz, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `radial-gradient(circle at 35% 30%, ${alpha(s.color, 0.95)}, ${alpha(s.color, 0.55)})`,
                      border: `2px solid ${alpha(s.color, you ? 1 : 0.7)}`, boxShadow: `0 0 ${you ? 22 : 14}px ${alpha(s.color, you ? 0.7 : 0.45)}` }}>
                      <Mono style={{ fontSize: compact ? (you ? 15 : 13) : (you ? 17 : 14), fontWeight: 700, color: '#0A0B0E' }}>{calm || voided ? '·' : rk}</Mono>
                    </div>
                  )}
                  {lead && (
                    <div style={{ position: 'absolute', top: -17, left: '50%', transform: 'translateX(-50%)' }}>
                      <LIcon name="crown" size={crownSize} color={LTOKENS.gold} stroke={2} />
                    </div>
                  )}
                </div>
                {!calm && !voided && (
                  showHead && compact ? (
                    /* COMPACT head: the score label sits at a FIXED offset directly BELOW
                       the head, centred, clearing the headSz footprint (top:100% = footprint
                       bottom). This keeps it inside the head's OWN lane, so a bigger head's
                       label can't reach into the neighbour lane (the beside-label overlap).
                       It is DECORATION offset from the head — never the anchor; the face
                       centre stays the P&L anchor. Desktop keeps the beside label: its 316px
                       lanes never overlap even at headSz, and a below-label would collide
                       with the desktop-only under-head name row at low altitudes. */
                    <div style={{ position: 'absolute', left: '50%', top: `calc(100% + ${LABEL_BELOW_GAP}px)`, transform: 'translateX(-50%)', whiteSpace: 'nowrap',
                      // the below-label sits over its own lane's climb trail line — a bg-toned
                      // shadow keeps the digits legible over the coloured stroke.
                      textShadow: `0 1px 4px ${LTOKENS.bg}` }}>
                      <ArenaCount value={at(s)} size={you ? 15 : 12} weight={700} showSign={false} />
                    </div>
                  ) : (
                    <div style={{ position: 'absolute', left: dz / 2 + 11, top: '50%', transform: 'translateY(-50%)', whiteSpace: 'nowrap' }}>
                      <ArenaCount value={at(s)} size={compact ? (you ? 15 : 12) : (you ? 18 : 14)} weight={700} showSign={false} />
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* The cut label renders AFTER the orbs so it WINS the paint overlap — a
          live-day orb parked at the cut altitude can't cover it. It still sits
          UNDER the personal "to the cut" gap pill and the fly-up token, which paint
          after it. Gated on hasCut → absent at rest (awaiting / Day-0, every seat
          at zero), so it never draws through the base-camp orb band. */}
      {hasCut && (
        <div style={{ position: 'absolute', left: axisW + 6, top: Y(cutAlt) - 18, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: alpha(LX.cut, 0.12), border: `1px solid ${alpha(LX.cut, 0.4)}` }}>
            <LIcon name="flag" size={10} color={LX.cut} stroke={2} />
            <Mono style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.1em', color: LX.cut }}>CUT · TOP 2 ADVANCE</Mono>
          </span>
        </div>
      )}

      {/* you → cut gap + the fly-up token (live) */}
      {live && youRow && (() => {
        const yi = rows.findIndex((s) => s.id === youId);
        const yx = laneX(yi); const yy = Y(at(youRow));
        const youRank = rankOf(youId);
        const gap = ranked && youRank > 2 ? +(cutAlt - at(youRow)).toFixed(1) : null;
        const flyLabel = surge ? (typeof surge.pts === 'number' ? fmtPoints(surge.pts) : surge.pts) : null;
        // The head stands ~0.54*size taller than the orb (the antenna), so the "to the cut"
        // pill is raised to clear the taller you-head's bulb when a head renders. You trail
        // here (youRank>2) → never the leader, so the you head is the non-leader headSz.
        // Flag-off keeps the original yy-42 (byte-identical).
        const youHeadSz = headSizeFor(true, false, compact);
        const cutPillTop = showHead ? yy - Math.round(0.542 * youHeadSz) - 24 : yy - 42;
        return (
          <>
            {gap != null && gap > 0 && (
              <div className={reduce ? '' : 'bv2-rise'} style={{ position: 'absolute', left: yx, top: cutPillTop, transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 999, background: alpha(LX.cut, 0.12), border: `1px solid ${alpha(LX.cut, 0.4)}` }}>
                  <LIcon name="long" size={9} color={LX.cut} stroke={2.4} />
                  <Mono style={{ fontSize: 9, fontWeight: 700, color: LX.cut }}>{gap} to the cut</Mono>
                </span>
              </div>
            )}
            {surge && surge.key && !reduce && (
              <div key={`fly${surge.key}`} className="bv2-fly" style={{ position: 'absolute', left: yx, top: plotB - 10, transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 5 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 999, background: alpha(ST_GOOD, 0.95), boxShadow: `0 0 16px ${alpha(ST_GOOD, 0.8)}` }}>
                  <LIcon name="bolt" size={11} color="#0A0B0E" stroke={2.4} />
                  <Mono style={{ fontSize: 13, fontWeight: 800, color: '#0A0B0E' }}>{flyLabel}</Mono>
                </span>
              </div>
            )}
          </>
        );
      })()}

      {calm && (
        <div style={{ position: 'absolute', top: padT - 6, left: 0, right: 0, textAlign: 'center' }}>
          <Mono style={{ fontSize: 11, letterSpacing: '0.16em', color: LTOKENS.ink3, textTransform: 'uppercase' }}>The climb begins at the bell</Mono>
        </div>
      )}
      {voided && !calm && (
        <div style={{ position: 'absolute', top: padT - 6, left: 0, right: 0, textAlign: 'center' }}>
          <Mono style={{ fontSize: 11, letterSpacing: '0.16em', color: LTOKENS.ink3, textTransform: 'uppercase' }}>Voided — no result recorded</Mono>
        </div>
      )}
    </div>
  );
}
